const INPOST_ATTRIBUTE_KEYS = Object.freeze({
  method: 'Dostawa do punktu',
  point: 'Paczkomat InPost',
  address: 'Adres Paczkomatu',
  postCode: 'Kod pocztowy Paczkomatu',
  city: 'Miasto Paczkomatu',
});

function cleanText(value, maximumLength = 180) {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, maximumLength);
}

export function normalizeInpostPoint(point) {
  if (!point || typeof point !== 'object') {
    throw new Error('Nieprawidłowe dane Paczkomatu.');
  }

  const name = cleanText(point.name, 80);
  if (!/^[a-z0-9][a-z0-9_-]{2,79}$/iu.test(name)) {
    throw new Error('Paczkomat nie ma prawidłowego identyfikatora.');
  }

  const pointTypes = Array.isArray(point.type) ? point.type.map((type) => cleanText(type, 40)) : [];
  if (!pointTypes.includes('parcel_locker')) {
    throw new Error('Wybrany punkt nie jest Paczkomatem. Wybierz automat Paczkomat InPost.');
  }

  const status = cleanText(point.status, 40);
  if (status && status.toLowerCase() !== 'operating') {
    throw new Error('Ten Paczkomat jest obecnie niedostępny. Wybierz inny punkt.');
  }

  const details = point.address_details && typeof point.address_details === 'object'
    ? point.address_details
    : {};
  const address = point.address && typeof point.address === 'object' ? point.address : {};
  const city = cleanText(details.city || point.city || address.city, 100);
  const postCode = cleanText(details.post_code || details.postCode || point.post_code, 20);
  const street = cleanText(details.street || point.street, 120);
  const buildingNumber = cleanText(details.building_number || details.buildingNumber, 30);
  const line1 = cleanText(address.line1, 160) || cleanText([street, buildingNumber].filter(Boolean).join(' '), 160);
  const line2 = cleanText(address.line2, 160) || cleanText([postCode, city].filter(Boolean).join(' '), 160);
  const fullAddress = cleanText([line1, line2].filter(Boolean).join(', '), 220);

  if (!fullAddress) {
    throw new Error('Nie udało się odczytać adresu Paczkomatu. Wybierz punkt ponownie.');
  }

  return { name, address: fullAddress, postCode, city };
}

export function inpostCartAttributes(selection, deliveryChoice = '') {
  const attributes = Object.fromEntries(Object.values(INPOST_ATTRIBUTE_KEYS).map((key) => [key, '']));
  if (deliveryChoice === 'standard-shipping') {
    attributes[INPOST_ATTRIBUTE_KEYS.method] = 'Nie';
    return attributes;
  }
  if (deliveryChoice === 'parcel-locker' || selection) {
    attributes[INPOST_ATTRIBUTE_KEYS.method] = 'Paczkomat InPost';
  }
  if (!selection) return attributes;
  attributes[INPOST_ATTRIBUTE_KEYS.point] = selection.name;
  attributes[INPOST_ATTRIBUTE_KEYS.address] = selection.address;
  attributes[INPOST_ATTRIBUTE_KEYS.postCode] = selection.postCode;
  attributes[INPOST_ATTRIBUTE_KEYS.city] = selection.city;
  return attributes;
}

let geowidgetAssetsPromise;

function loadGeowidgetAssets(scriptUrl, stylesheetUrl) {
  if (customElements.get('inpost-geowidget')) return Promise.resolve();
  if (geowidgetAssetsPromise) return geowidgetAssetsPromise;

  geowidgetAssetsPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-allprod-inpost="stylesheet"]')) {
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = stylesheetUrl;
      stylesheet.dataset.allprodInpost = 'stylesheet';
      document.head.append(stylesheet);
    }

    const finish = () => customElements.whenDefined('inpost-geowidget').then(resolve, reject);
    const existing = document.querySelector('script[data-allprod-inpost="script"]');
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error', reject, { once: true });
      finish();
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.defer = true;
    script.dataset.allprodInpost = 'script';
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('Nie udało się wczytać mapy InPost.')), { once: true });
    document.head.append(script);
  });
  return geowidgetAssetsPromise;
}

if (typeof document !== 'undefined' && !customElements.get('allprod-inpost-picker')) {
  class AllprodInpostPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.toggle = this.querySelector('[data-inpost-toggle]');
      this.standardShipping = this.querySelector('[data-inpost-standard-shipping]');
      this.selectionPanel = this.querySelector('[data-inpost-selection]');
      this.selectionName = this.querySelector('[data-inpost-selection-name]');
      this.selectionAddress = this.querySelector('[data-inpost-selection-address]');
      this.openButton = this.querySelector('[data-inpost-open]');
      this.dialog = this.querySelector('[data-inpost-dialog]');
      this.mapHost = this.querySelector('[data-inpost-map]');
      this.closeButton = this.querySelector('[data-inpost-close]');
      this.status = this.querySelector('[data-inpost-status]');
      this.pendingSave = null;
      this.selection = this.readInitialSelection();

      this.toggle?.addEventListener('change', () => this.onToggle());
      this.standardShipping?.addEventListener('change', () => this.onToggle());
      this.openButton?.addEventListener('click', () => this.openMap());
      this.closeButton?.addEventListener('click', () => this.closeMap());
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeMap();
      });
      this.render();
    }

    readInitialSelection() {
      const name = cleanText(this.dataset.selectedName, 80);
      const address = cleanText(this.dataset.selectedAddress, 220);
      if (!name || !address) return null;
      return {
        name,
        address,
        postCode: cleanText(this.dataset.selectedPostCode, 20),
        city: cleanText(this.dataset.selectedCity, 100),
      };
    }

    render() {
      const enabled = this.deliveryChoice() === 'parcel-locker';
      if (this.selectionPanel) this.selectionPanel.hidden = !enabled;
      if (this.selectionName) this.selectionName.textContent = this.selection?.name || 'Nie wybrano Paczkomatu';
      if (this.selectionAddress) this.selectionAddress.textContent = this.selection?.address || '';
      if (this.openButton) this.openButton.textContent = this.selection ? 'Zmień Paczkomat' : 'Wybierz Paczkomat';

      window.requestAnimationFrame(() => {
        const summary = this.closest('.cart-summary__inner, .cart-drawer__summary') || this.parentElement;
        const accelerated = summary?.querySelector('.additional-checkout-buttons');
        const invoiceRequested = Boolean(summary?.querySelector('vat-invoice-fields [data-vat-toggle]')?.checked);
        if (accelerated) accelerated.hidden = enabled || invoiceRequested;
      });
    }

    async onToggle() {
      this.status.textContent = '';
      const choice = this.deliveryChoice();
      if (choice === 'standard-shipping') {
        this.selection = null;
        this.render();
        await this.saveSelection();
        return;
      }
      this.render();
      await this.saveSelection();
      if (choice === 'parcel-locker' && !this.selection) await this.openMap();
    }

    async openMap() {
      if (!this.dataset.token) {
        this.status.textContent = 'Mapa Paczkomatów nie jest jeszcze skonfigurowana.';
        return;
      }
      this.status.textContent = 'Wczytywanie mapy Paczkomatów…';
      try {
        await loadGeowidgetAssets(this.dataset.scriptUrl, this.dataset.stylesheetUrl);
        if (!this.mapHost.querySelector('inpost-geowidget')) {
          const widget = document.createElement('inpost-geowidget');
          widget.setAttribute('token', this.dataset.token);
          widget.setAttribute('config', 'parcelCollect');
          widget.setAttribute('language', 'pl');
          widget.setAttribute('country', 'PL');
          widget.addEventListener('inpost.geowidget.init', (event) => {
            const api = event.detail?.api;
            api?.changePointsType('PARCEL_LOCKER');
            api?.addPointSelectedCallback((point) => this.selectPoint(point));
          }, { once: true });
          this.mapHost.append(widget);
        }
        this.status.textContent = '';
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      } catch (error) {
        this.status.textContent = error?.message || 'Nie udało się wczytać mapy InPost.';
      }
    }

    closeMap() {
      this.render();
      if (this.dialog?.open && typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog?.removeAttribute('open');
    }

    async selectPoint(point) {
      try {
        this.selection = normalizeInpostPoint(point);
        this.render();
        this.status.textContent = 'Zapisywanie Paczkomatu…';
        await this.saveSelection();
        this.status.textContent = 'Paczkomat został zapisany w zamówieniu.';
        if (this.dialog?.open && typeof this.dialog.close === 'function') this.dialog.close();
      } catch (error) {
        this.status.textContent = error?.message || 'Nie udało się zapisać Paczkomatu.';
      }
    }

    validateSelection() {
      const choice = this.deliveryChoice();
      if (!choice) {
        this.status.textContent = 'Wybierz, czy zamówienie ma być wysłane do Paczkomatu.';
        this.toggle?.focus();
        return false;
      }
      if (choice === 'standard-shipping') return true;
      if (this.selection) return true;
      this.status.textContent = 'Wybierz Paczkomat przed przejściem do płatności.';
      this.openMap();
      return false;
    }

    async saveSelection() {
      if (this.pendingSave) await this.pendingSave;
      this.pendingSave = fetch(this.dataset.updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: inpostCartAttributes(this.selection, this.deliveryChoice()) }),
      });
      try {
        const response = await this.pendingSave;
        if (!response.ok) throw new Error('Nie udało się zapisać Paczkomatu. Spróbuj ponownie.');
      } finally {
        this.pendingSave = null;
      }
    }

    deliveryChoice() {
      if (this.toggle?.checked) return 'parcel-locker';
      if (this.standardShipping?.checked) return 'standard-shipping';
      return '';
    }
  }

  customElements.define('allprod-inpost-picker', AllprodInpostPicker);

  const bypassedForms = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;
    const form = event.target;
    if (bypassedForms.has(form)) {
      bypassedForms.delete(form);
      return;
    }
    const picker = form.querySelector('allprod-inpost-picker')
      || form.closest('.cart-summary__inner, .cart-drawer__summary')?.querySelector('allprod-inpost-picker')
      || document.querySelector('allprod-inpost-picker');
    if (!picker) return;

    event.preventDefault();
    if (!picker.validateSelection()) return;
    submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');
    try {
      if (picker.pendingSave) await picker.pendingSave;
      bypassedForms.add(form);
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
      form.requestSubmit(submitter);
    } catch (error) {
      picker.status.textContent = 'Nie udało się zapisać Paczkomatu. Spróbuj ponownie.';
      submitter.disabled = false;
      submitter.removeAttribute('aria-busy');
    }
  }, true);
}
